import React from "react";

import InfoSM from "@busbud/design-system-components/dist/Icons/Info/SM";
import type { Props as LabelProps } from "@busbud/design-system-components/dist/Label";
import Label from "@busbud/design-system-components/dist/Label";
import OriginDestinationLabel from "@busbud/design-system-components/dist/OriginDestinationLabel";
import Stack from "@busbud/design-system-components/dist/Stack";

import { useAppContext } from "@app/helpers/hooks";
import type { CreditsActivity } from "@app/types/loyalty";

import { LocalizedPrice } from "../../../localized-price";
import { TripItemImage } from "../../../trip-item-image";
import { useStyles } from "../credits-activity.styles";

interface Props {
  activity: CreditsActivity;
}

export const CreditsActivityItem: React.FC<Props> = ({ activity }) => {
  const { intlProvider, device } = useAppContext();
  const { classes } = useStyles();

  const {
    origin_city,
    destination_city,
    is_roundtrip,
    is_cancelled,
    travel_date
  } = activity;

  const metatadata_props: Partial<LabelProps> = {
    size: {
      xs: "sm",
      md: "md"
    },
    fontWeight: "bold",
    noWrap: true
  };

  const price_label_props: Partial<LabelProps> = {
    size: {
      xs: "md",
      md: "xl"
    },
    fontWeight: "bold",
    whiteSpace: "nowrap"
  };

  return (
    <Stack spacing={{ xs: 1.5, md: 2 }} alignItems="center">
      <TripItemImage
        image_url={destination_city.image_url}
        archived={is_cancelled}
        className="hidden sm:block"
      />
      <Stack
        classes={{
          root: classes.container
        }}
        justifyContent="space-between"
        alignItems="center"
        direction="row"
        flexGrow={1}
      >
        <Stack
          classes={{
            root: classes.destinationContainer
          }}
          direction="column"
          spacing={0.5}
        >
          <OriginDestinationLabel
            size={device.is_mobile ? "sm" : "md"}
            origin={origin_city.name}
            destination={destination_city.name}
            variant={is_roundtrip ? "roundtrip" : "one-way"}
            classes={{
              root: classes.destinationLabel,
              icon: classes.destinationIcon
            }}
          />
          <Stack spacing={1.5}>
            <Label {...metatadata_props} color="text.secondary.opaque">
              {intlProvider.formatDate(travel_date, {
                month: "short",
                day: "numeric"
              })}
            </Label>
            {!!is_cancelled && (
              <Label
                {...metatadata_props}
                color="text.tertiary.opaque"
                startIcon={<InfoSM />}
              >
                Trip cancelled
              </Label>
            )}
          </Stack>
        </Stack>
        <Stack
          direction="column"
          spacing={0.5}
          classes={{
            root: classes.price
          }}
        >
          {!!activity.rewarded_amount && (
            <Label {...price_label_props} color="palette.green.regular.700">
              +
              <LocalizedPrice
                currency={activity.rewarded_amount.currency}
                price_in_cents={activity.rewarded_amount.value}
              />
            </Label>
          )}
          {!!activity.redeemed_amount && (
            <Label {...price_label_props} color="palette.red.muted.600">
              -
              <LocalizedPrice
                currency={activity.redeemed_amount.currency}
                price_in_cents={activity.redeemed_amount.value}
              />
            </Label>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
